import cloneDeep from 'lodash/cloneDeep';
import { OrgSelection, CheckBox } from 'types/appContext';
import { UserProfile } from 'types/authContext';
import { CountriesList, OrgBusinessUnit, OrgData } from 'types/dataContext';
import { OMFilter as OMFilterCategory } from 'types/enums';
import { filterCCByUnitCode } from 'utils/filters';
import { getShortCC } from 'utils/text';
import { isNullOrUndefined } from 'utils/validation';

export const getEmptyOrgSelection: () => OrgSelection = () => ({
    selections: {
        countrySelection: [],
        unitSelection: [],
        divisionSelection: [],
        departmentSelection: [],
        costCentreSelection: [],
    },
    utils: {
        selectedCostCentres: [],
        allCostCentres: [],
        hierarchy: null,
    }
});

export type OrgActionCountries = {
    type: 'populateCountries';
    countries: CountriesList;
    homeCountry: string | undefined;
};
export type OrgActionUnit = { type: 'populateOrgData'; orgData: OrgData; homeUnit: string | undefined };
export type OrgActionSelection = {
    type: 'updateSelection';
    orgData: OrgData | undefined;
    category: OMFilterCategory;
    unitType?: string; // Only used for unit selection due to some units have same id's
    key: string;
    isSelectAll?: boolean;
};
export type OrgAction = OrgActionCountries | OrgActionUnit | OrgActionSelection;

export const ORG_SELECTION_SELECT_ALL = 'select-all'; // Special value to indicate that all items should be selected or deselected

/**
 * Filters array of objects based on value of defaultChecked. If defaultChecked is false for all objects, it does not filter away any objects.
 * It then returns the value of the key, passed as param.
 * @param data Array of objects with a defaultChecked property
 * @param key The key of the object to return
 * @returns Array of values of the key
 * @example getValueOfChecked([{ defaultChecked: true, value: 'a' }, { defaultChecked: false, value: 'b' }], 'value') // returns ['a']
 */
export const getValueOfChecked = <
    TObj extends { defaultChecked: boolean },
    TKey extends keyof TObj,
>(
    data: Array<TObj>,
    key: TKey
): Array<TObj[TKey]> => (data.some(({ defaultChecked }) => defaultChecked)
    ? data.filter(({ defaultChecked }) => defaultChecked).map(obj => obj[key])
    : data.map(obj => obj[key]));

/** * Returns the selection for the country based on the action.
 * @param action The action containing the countries and home country.
 * @return An array of CheckBox objects representing the country selection.
 * */
const getCountrySelection = (action: OrgActionCountries) => action.countries.map(({ code, name }) => ({
    autocomplete: true,
    id: code,
    category: OMFilterCategory.COUNTRY,
    label: name,
    defaultChecked: code === action.homeCountry,
    value: code,
}));

const updateCountrySelection = (oldState: OrgSelection, action: OrgActionSelection) => oldState.selections.countrySelection.map(previousState => ({
    ...previousState,
    defaultChecked: previousState.id === action.key,
}));

const getUnitSelection = (action: OrgActionUnit) => {
    /**
     * Flag to indicate if the defaultChecked has been set for the first time.
     * This is needed to ensure that only one unit is selected by default or defaultChecked is set only once.
     * If there are multiple units with the same code, only the first one should be selected.
     */
    let defaultCheckedSet = false;

    return action.orgData.businessUnits.map((unit: OrgBusinessUnit) => {
        const defaultChecked = !defaultCheckedSet && unit.code === action.homeUnit;
        if (defaultChecked) {
            defaultCheckedSet = true;
        }

        return {
            autocomplete: true,
            id: unit.id,
            category: OMFilterCategory.UNIT,
            type: unit.type,
            label: unit.name,
            defaultChecked,
            value: unit.code,
        };
    });
};

const updateUnitSelection = (oldState: OrgSelection, action: OrgActionSelection) => oldState.selections.unitSelection.map(prevState => ({
    ...prevState,
    defaultChecked: prevState.value === action.key && prevState.type === action.unitType,
}));

const getDivisionSelection = (orgData: OrgData, unitSelection: OrgSelection['selections']['unitSelection']): CheckBox[] => {
    const selectedUnitId = unitSelection.find(({ defaultChecked }) => defaultChecked)?.id;

    return orgData.divisions?.filter(({ parentId }) => parentId === selectedUnitId)?.map(({ id, code, name }) => ({
        autocomplete: true,
        id,
        category: OMFilterCategory.DIVISION,
        label: name,
        defaultChecked: false,
        value: code,
    }));
};

const updateDivisionSelection = (oldState: OrgSelection, action: OrgActionSelection) => {
    if (action.key === ORG_SELECTION_SELECT_ALL) {
        const areAllSelected = oldState.selections.divisionSelection.every(div => div.defaultChecked);
        const selection = oldState.selections.divisionSelection.map(div => ({
            ...div,
            defaultChecked: !areAllSelected,
        }));

        return selection;
    }

    return oldState.selections.divisionSelection.map(div => ({
        ...div,
        defaultChecked: div.value === action.key ? !div.defaultChecked : div.defaultChecked,
    }));
};

const getDepartmentSelection = (orgData: OrgData, divisionSelection: OrgSelection['selections']['divisionSelection']) => {
    const divisionIds = getValueOfChecked(divisionSelection, 'id');

    return orgData.departments.filter(({ parentId }) => divisionIds.includes(parentId))?.map(({ id, name, code }) => ({
        autocomplete: true,
        id,
        category: OMFilterCategory.DEPARTMENT,
        label: name,
        defaultChecked: false,
        value: code,
    }));
};

/**
 * * Disabling this code because we dont update departments from UI due to the departments hidden in the filter.
 * * If we need to update departments in the future, we can enable this code.
 * Updates the department selection based on the action.
 * @param oldState The previous state of the OrgSelection.
 * @param action The action containing the key to update.
 * If the key matches, it toggles the defaultChecked state of the department.
 * If the key is 'select-all', it toggles all departments based on the current state
 * @returns
    const updateDepartmentSelection = (oldState: OrgSelection, action: OrgActionSelection) => oldState.selections.departmentSelection.map(dept => ({
        ...dept,
        defaultChecked: dept.value === action.key ? !dept.defaultChecked : dept.defaultChecked,
    }));
 */

const getCostCentreSelection = (
    orgData: OrgData,
    departmentSelection: OrgSelection['selections']['departmentSelection'],
    unitSelection: OrgSelection['selections']['unitSelection']
) => {
    if (!departmentSelection.length) return [];
    const departmentIds = getValueOfChecked(departmentSelection, 'id');
    const costCentres = orgData.costCentres
        .filter(({ parentId }) => departmentIds.includes(parentId))
        .filter((cc, index, self) => self.findIndex(c => c.id === cc.id && c.parentId === cc.parentId) === index);
    const selectedUnit = unitSelection.find(({ defaultChecked }) => defaultChecked)?.value ?? '';
    // Remove duplicate cost centre objects by id
    const uniqueCostCentres = filterCCByUnitCode(costCentres, selectedUnit);

    const costCentreSelection: CheckBox[] = uniqueCostCentres?.map(({ id, name, parentId }) => ({
        autocomplete: true,
        id: parentId, // Using ParentID as ID for now. Because Cost center does not have a unique ID
        category: OMFilterCategory.COST_CENTER,
        label: name,
        defaultChecked: false,
        value: id,
        subLabel: getShortCC(id),
    }));

    return costCentreSelection;
};
const updateCostCentreSelection = (oldState: OrgSelection, action: OrgActionSelection) => {
    if (action.key === ORG_SELECTION_SELECT_ALL) {
        const areAllSelected = oldState.selections.costCentreSelection.every(costCentre => costCentre.defaultChecked);
        const selection = oldState.selections.costCentreSelection.map(costCentre => ({
            ...costCentre,
            defaultChecked: !areAllSelected,
        }));

        return selection;
    }

    return oldState.selections.costCentreSelection.map(costCentre => ({
        ...costCentre,
        defaultChecked: costCentre.value === action.key ? !costCentre.defaultChecked : costCentre.defaultChecked,
    }));
};

const getCostCentres = (costCentreSelection: CheckBox[]): OrgSelection['utils']['selectedCostCentres'] => {
    if (!costCentreSelection.length) return [];
    // Get the selected cost centre codes from the selection
    const selectedCostCentres = costCentreSelection.filter(cc => cc.defaultChecked);
    // Get the default checked cost centres
    const costCenters = selectedCostCentres.length > 0 ? selectedCostCentres : costCentreSelection;

    return costCenters.map(costCentre => ({
        costCentreCode: costCentre.value,
        costCentreDescription: costCentre.label,
        costCentreParentId: Number(costCentre.id),
    }));
};

/**
 * Filters the org data based on the selected unit and the chain of parentId's, to remove any divisions/costCentres/departments outside of the unit.
 * Needed in case some of the codes are duplicated, real example division codes in CH.
 * Then it is not possible to filter the org data based on the code, but instead it needs to be done using parentId's.
 */
const filterOrgDataForUnit = (orgData: OrgData, unitSelection: OrgSelection['selections']['unitSelection']): OrgData => {
    const selectedUnit = unitSelection.find(({ defaultChecked }) => defaultChecked);

    const divisionsUnderUnit = orgData.divisions.filter(({ parentId }) => parentId === selectedUnit?.id);
    const divisionIds = new Set(divisionsUnderUnit.map(({ id }) => id)); // TODO: check with backend and remove this if not needed
    const departmentsUnderUnit = orgData.departments.filter(({ parentId }) => divisionIds.has(parentId));
    const subDepartmentsUnderUnit = orgData.subDepartments?.filter(({ parentId }) => departmentsUnderUnit.some(({ id }) => id === parentId));
    const costCentresUnderUnit = orgData.costCentres.filter(({ parentId }) => departmentsUnderUnit.some(({ id }) => id === parentId));

    return {
        country: orgData.country,
        businessUnits: orgData.businessUnits,
        divisions: divisionsUnderUnit,
        departments: departmentsUnderUnit,
        costCentres: costCentresUnderUnit,
        subDepartments: subDepartmentsUnderUnit,
    };
};

const getOrgHierarchy = (orgSelection: OrgSelection, orgData: OrgData) => ({
    divisionHierarchy: orgSelection.selections.divisionSelection.map(division => {
        const departments = orgData.departments.filter(dep => dep.parentId === division.id);

        return ({
            division: orgData.divisions.find(({ id }) => id === division.id) ?? null,
            departments,
            costCentre: orgData.costCentres.filter(cc => departments.some(dep => dep.id === cc.parentId)) ?? null
        });
    }),
    costCentreHierarchy: orgSelection.utils.selectedCostCentres.map(costCentre => {
        const department = orgData.departments.filter(dep => dep.id === costCentre.costCentreParentId) ?? null;

        return {
            department: department ?? null,
            division: orgData.divisions.find(div => department?.some(dep => dep.parentId === div.id)) ?? null,
            costCentre
        };
    })
});

const orgSelector = (oldState: OrgSelection, action: OrgActionSelection): OrgSelection => {
    if (action.category === OMFilterCategory.COUNTRY) {
        const countrySelection = updateCountrySelection(oldState, action);

        const newState = getEmptyOrgSelection();
        newState.selections.countrySelection = countrySelection;

        return newState;
    }
    if (action.orgData === undefined) {
        // Can only select country if there is no org data
        return oldState;
    }
    if (action.category === OMFilterCategory.UNIT && !isNullOrUndefined(action.unitType)) {
        const newState = cloneDeep(oldState);

        newState.selections.unitSelection = updateUnitSelection(oldState, action);
        const { unitSelection } = newState.selections;
        // Filter the org data based on the selected unit. Fixed bug caused by CH having duplicate
        const unitOrgData = filterOrgDataForUnit(action.orgData, unitSelection);
        newState.selections.divisionSelection = getDivisionSelection(unitOrgData, unitSelection);
        newState.selections.departmentSelection = getDepartmentSelection(unitOrgData, newState.selections.divisionSelection);
        newState.selections.costCentreSelection = getCostCentreSelection(unitOrgData, newState.selections.departmentSelection, unitSelection);
        newState.utils.selectedCostCentres = getCostCentres(newState.selections.costCentreSelection);
        newState.utils.hierarchy = getOrgHierarchy(newState, action.orgData);
        newState.utils.allCostCentres = oldState.utils.allCostCentres;

        return newState;
    }
    if (action.category === OMFilterCategory.DIVISION) {
        const newState = cloneDeep(oldState);
        const { unitSelection } = newState.selections;
        newState.selections.divisionSelection = updateDivisionSelection(oldState, action);
        newState.selections.departmentSelection = getDepartmentSelection(action.orgData, newState.selections.divisionSelection);
        newState.selections.costCentreSelection = getCostCentreSelection(action.orgData, newState.selections.departmentSelection, unitSelection);
        newState.utils.selectedCostCentres = getCostCentres(newState.selections.costCentreSelection);
        newState.utils.allCostCentres = oldState.utils.allCostCentres;

        return newState;
    }
    if (action.category === OMFilterCategory.DEPARTMENT) {
        /**
         * Disabling this code because we dont update departments from UI due to the departments hidden in the filter.
         * If we need to update departments in the future, we can enable this code.
            const newState = cloneDeep(oldState);
            const { unitSelection } = newState.selections;
            newState.selections.departmentSelection = updateDepartmentSelection(oldState, action);
            newState.selections.costCentreSelection = getCostCentreSelection(action.orgData, newState.selections.departmentSelection, unitSelection);
            newState.utils.selectedCostCentres = getCostCentres(action.orgData, newState.selections.costCentreSelection);
            return newState;
         */

    }
    if (action.category === OMFilterCategory.COST_CENTER) {
        const newState = cloneDeep(oldState);

        newState.selections.costCentreSelection = updateCostCentreSelection(oldState, action);
        newState.utils.selectedCostCentres = getCostCentres(newState.selections.costCentreSelection);
        newState.utils.allCostCentres = oldState.utils.allCostCentres;

        return newState;
    }

    return oldState;
};

export const orgSelectionReducer = (oldState: OrgSelection, action: OrgAction): OrgSelection => {
    switch (action.type) {
    case 'populateCountries': {
        const newState = cloneDeep(oldState);
        newState.selections.countrySelection = getCountrySelection(action);

        return newState;
    }
    case 'populateOrgData': {
        const newState = cloneDeep(oldState);
        newState.selections.unitSelection = getUnitSelection(action);
        const { unitSelection } = newState.selections;
        newState.selections.divisionSelection = getDivisionSelection(action.orgData, unitSelection);
        newState.selections.departmentSelection = getDepartmentSelection(action.orgData, newState.selections.divisionSelection);
        newState.selections.costCentreSelection = getCostCentreSelection(action.orgData, newState.selections.departmentSelection, unitSelection);
        newState.utils.selectedCostCentres = getCostCentres(newState.selections.costCentreSelection);
        newState.utils.hierarchy = getOrgHierarchy(newState, action.orgData);
        newState.utils.allCostCentres = oldState.utils.allCostCentres;

        return newState;
    }
    case 'updateSelection': {
        return orgSelector(oldState, action);
    }

    default:
        return getEmptyOrgSelection();
    }
};

export const getHomeUnitId = (userBusinessUnit: UserProfile['businessUnit'] | undefined) => userBusinessUnit?.split('.')?.[1];
